<template>
  <div class="execution-logs-page">
    <Panel header="Фильтры" toggleable class="filters-panel">
      <div class="filters-grid">
        <div class="filter-field">
          <label>User ID</label>
          <InputText v-model="filters.user_id" class="w-full" />
        </div>
        <div class="filter-field">
          <label>Session ID</label>
          <InputText v-model="filters.session_id" class="w-full" />
        </div>
        <div class="filter-field">
          <label>Mode</label>
          <Select
            v-model="filters.mode"
            :options="modeOptions"
            show-clear
            placeholder="Все"
            class="w-full"
          />
        </div>
        <div class="filter-field">
          <label>From</label>
          <input v-model="filters.from" type="datetime-local" class="filter-datetime" />
        </div>
        <div class="filter-field">
          <label>To</label>
          <input v-model="filters.to" type="datetime-local" class="filter-datetime" />
        </div>
        <div class="filter-field">
          <label>Latency min</label>
          <InputNumber v-model="filters.latency_min" :use-grouping="false" class="w-full" />
        </div>
        <div class="filter-field">
          <label>Latency max</label>
          <InputNumber v-model="filters.latency_max" :use-grouping="false" class="w-full" />
        </div>
        <div class="filter-field">
          <label>Tokens min</label>
          <InputNumber v-model="filters.tokens_min" :use-grouping="false" class="w-full" />
        </div>
        <div class="filter-field">
          <label>Tokens max</label>
          <InputNumber v-model="filters.tokens_max" :use-grouping="false" class="w-full" />
        </div>
        <div class="filter-field filter-search">
          <label>Поиск (query / response)</label>
          <InputText v-model="filters.q" class="w-full" @input="onSearchInput" />
        </div>
      </div>
      <div class="filter-actions">
        <ButtonVue label="Применить" icon="pi pi-search" @click="applyFilters" />
        <ButtonVue label="Сбросить" severity="secondary" text @click="resetFilters" />
      </div>
    </Panel>

    <DataTable
      :value="items"
      lazy
      paginator
      :rows="pageSize"
      :total-records="total"
      :loading="loading"
      :rows-per-page-options="[10, 25, 50, 100]"
      data-key="id"
      sort-mode="single"
      :sort-field="sortField"
      :sort-order="sortOrder"
      removable-sort
      row-hover
      class="logs-table"
      @page="onPage"
      @sort="onSort"
      @row-click="onRowClick"
    >
      <Column field="created_at" header="Created" sortable style="min-width: 150px">
        <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
      </Column>
      <Column field="user_id" header="User" style="min-width: 100px" />
      <Column field="session_id" header="Session" style="min-width: 120px" class="col-session">
        <template #body="{ data }">
          <router-link
            v-if="data.session_id"
            :to="`/rag-monitor/sessions/${data.session_id}`"
            class="session-link"
            @click.stop
          >
            {{ truncate(data.session_id, 8) }}
          </router-link>
          <span v-else>—</span>
        </template>
      </Column>
      <Column field="mode" header="Mode" style="min-width: 90px" class="col-mode" />
      <Column field="chunks_count" header="Chunks" style="width: 80px" class="col-chunks" />
      <Column field="latency_ms" header="Latency" sortable style="width: 90px">
        <template #body="{ data }">{{ data.latency_ms ?? '—' }} ms</template>
      </Column>
      <Column field="tokens_used" header="Tokens" sortable style="width: 80px" />
      <Column field="query" header="Query" style="min-width: 180px">
        <template #body="{ data }">{{ truncate(data.query, 80) }}</template>
      </Column>
      <Column field="response_preview" header="Response" style="min-width: 160px" class="col-response">
        <template #body="{ data }">{{ truncate(data.response_preview, 60) }}</template>
      </Column>
    </DataTable>

    <LogDetailDialog v-model:visible="detailVisible" :log-id="selectedLogId" />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Panel from 'primevue/panel';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import { useToast } from 'primevue/usetoast';
import LogDetailDialog from '@/components/rag-monitor/LogDetailDialog.vue';
import { getExecutionLogs, getExecutionLogModes } from '@/services/ragMonitorApi';

const toast = useToast();
const loading = ref(false);
const items = ref([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(25);
const sortField = ref('created_at');
const sortOrder = ref(-1);
const modeOptions = ref([]);
const detailVisible = ref(false);
const selectedLogId = ref(null);

const filters = ref({
  user_id: '',
  session_id: '',
  mode: null,
  from: '',
  to: '',
  latency_min: null,
  latency_max: null,
  tokens_min: null,
  tokens_max: null,
  q: ''
});

let searchDebounce = null;

function truncate(s, n) {
  if (!s) return '—';
  const str = String(s);
  return str.length <= n ? str : `${str.slice(0, n)}…`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('ru-RU');
}

function buildParams() {
  const f = filters.value;
  const params = {
    page: page.value,
    pageSize: pageSize.value,
    sort: sortField.value,
    order: sortOrder.value === 1 ? 'asc' : 'desc'
  };
  if (f.user_id) params.user_id = f.user_id;
  if (f.session_id) params.session_id = f.session_id;
  if (f.mode) params.mode = f.mode;
  if (f.from) params.from = new Date(f.from).toISOString();
  if (f.to) params.to = new Date(f.to).toISOString();
  if (f.latency_min != null && f.latency_min !== '') params.latency_min = f.latency_min;
  if (f.latency_max != null && f.latency_max !== '') params.latency_max = f.latency_max;
  if (f.tokens_min != null && f.tokens_min !== '') params.tokens_min = f.tokens_min;
  if (f.tokens_max != null && f.tokens_max !== '') params.tokens_max = f.tokens_max;
  if (f.q?.trim()) params.q = f.q.trim();
  return params;
}

async function loadLogs() {
  loading.value = true;
  try {
    const data = await getExecutionLogs(buildParams());
    items.value = data.items || [];
    total.value = data.total || 0;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: e.message, life: 5000 });
  } finally {
    loading.value = false;
  }
}

async function loadModes() {
  try {
    const data = await getExecutionLogModes();
    modeOptions.value = data.modes || [];
  } catch {
    modeOptions.value = [];
  }
}

function onPage(event) {
  page.value = (event.page ?? 0) + 1;
  pageSize.value = event.rows ?? pageSize.value;
  loadLogs();
}

function onSort(event) {
  sortField.value = event.sortField || 'created_at';
  sortOrder.value = event.sortOrder ?? -1;
  page.value = 1;
  loadLogs();
}

function applyFilters() {
  page.value = 1;
  loadLogs();
}

function resetFilters() {
  filters.value = {
    user_id: '',
    session_id: '',
    mode: null,
    from: '',
    to: '',
    latency_min: null,
    latency_max: null,
    tokens_min: null,
    tokens_max: null,
    q: ''
  };
  page.value = 1;
  loadLogs();
}

function onSearchInput() {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    page.value = 1;
    loadLogs();
  }, 300);
}

function onRowClick(event) {
  selectedLogId.value = event.data.id;
  detailVisible.value = true;
}

onMounted(() => {
  loadModes();
  loadLogs();
});
</script>

<style scoped>
.execution-logs-page {
  padding-bottom: 1.5rem;
}

.filters-panel {
  margin-bottom: 1rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.filter-field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: var(--p-text-muted-color);
}

.filter-search {
  grid-column: 1 / -1;
}

.filter-datetime {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--p-inputtext-border-color);
  border-radius: var(--p-inputtext-border-radius, 6px);
  background: var(--p-inputtext-background);
  color: var(--p-inputtext-color);
  font: inherit;
  color-scheme: light;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

.logs-table :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.session-link {
  color: var(--p-primary-color);
  text-decoration: none;
}

.session-link:hover {
  text-decoration: underline;
}

.w-full {
  width: 100%;
}

@media (max-width: 768px) {
  .col-chunks,
  .col-response {
    display: none;
  }
}
</style>
